import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Form, Input, InputNumber, Radio, Segmented, Select, Skeleton, message } from 'antd';
import type { DatePickerProps } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SectionCard from './components/SectionCard';
import { ActivityStatusTag } from './components/ActivityStatusTag';
import BannerUpload from './components/BannerUpload';
import RuleConfigTable from './components/RuleConfigTable';
import RuleTextEditor from './components/RuleTextEditor';
import RuleTextPreviewModal, { RuleTextPreviewSource } from './components/RuleTextPreviewModal';
import { confirmOnline } from './components/onlineConfirm';
import { useRuleTemplate } from './hooks/useRuleTemplate';
import { useSiteOptions } from './hooks/useSiteOptions';
import { useImeSafeCount } from './hooks/useImeSafeCount';
import { useWeekRangePicker } from './hooks/useWeekRangePicker';
import { GET_DRIVER_ACTIVITY_ADMIN_DETAIL, GetDriverActivityAdminDetailResponse, GetDriverActivityAdminDetailVariables } from './graphql/queries';
import {
    SAVE_DRIVER_ACTIVITY_DRAFT,
    SUBMIT_DRIVER_ACTIVITY_ONLINE,
    SaveDriverActivityDraftResponse,
    SaveDriverActivityVariables,
    SubmitDriverActivityOnlineResponse,
    UPDATE_DRIVER_ACTIVITY_LIMITED,
    UpdateDriverActivityLimitedResponse,
    UpdateDriverActivityLimitedVariables,
} from './graphql/mutations';
import { DriverActivityAdminStatus, DriverActivityType } from './graphql/types';
import {
    ActivityFormValues,
    DATE_FORMAT,
    DEADLINE_FORMAT,
    MAX_WEEKS,
    NAME_MAX_LENGTH,
    RuleDraft,
    amountToString,
    buildLimitedInput,
    buildSaveInput,
    canEditActivity,
    computeWeekCount,
    detailToFormValues,
    getDefaultFormValues,
    isLimitedEditStatus,
    isMonday,
    isStartDateTodayLa,
    isSunday,
    rulesToInput,
    validateDateRange,
    validateMaxReward,
    validateName,
    validateRules,
    validateSignUpDeadline,
} from './utils/activityFormUtils';
import { RuleTextDraft, ruleTextFromList, ruleTextToInput, sectionTitle, validateRuleText } from './utils/ruleTextUtils';
import { submitWithTranslationStaleConfirm } from './utils/submitHelpers';
import { LIST_PATH, listPathFromState } from './utils/listQuery';
import { showError } from '../../../utils/exceptionUtils';

type SubmitKind = 'draft' | 'online' | 'limited';

/** 未开始 / 进行中可改的字段 */
const LIMITED_FIELDS = ['bannerS3Key', 'rules', 'ruleText'];

/** 日历里把「今天」主色描边加粗：周模式下今天多半是禁用格，antd 自带的 today 样式在禁用格上几乎看不见（10-20 用户反馈不好找） */
const renderTodayCell: NonNullable<DatePickerProps['cellRender']> = (current, info) => {
    if (info.type !== 'date' || !dayjs.isDayjs(current) || !current.isSame(info.today, 'day')) {
        return info.originNode;
    }
    return (
        // 只描边加粗、不写字色：选中态由 antd 的 .ant-picker-cell-selected 给白字紫底，内联字色会把它盖掉（10-22 选今天看不见日期）
        <div className="ant-picker-cell-inner" style={{ border: '1px solid #722ed1', fontWeight: 600 }}>
            {current.date()}
        </div>
    );
};

const HelpText: React.FC<{ children: React.ReactNode; error?: boolean }> = ({ children, error }) => (
    <span className={`text-[12.5px] ${error ? 'text-[#F0453A]' : 'text-[#8C8C8C]'}`}>{children}</span>
);

/**
 * 新建 / 编辑活动（原型页面 2），四张卡：基本信息 / 参与条件 / 奖励活动规则配置 / 规则文案。
 * 暂存全可改；未开始 / 进行中只能改文案、Banner、在已有规则里新增 Hub/WHS（走 updateDriverActivityLimited）。
 * 保存草稿与提交上线前端做同一套校验；提交上线多一步确认弹窗。
 */
const DriverActivityFormPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    // 从列表进来时带着列表的筛选，返回 / 保存后回列表时原样带回
    const listPath = listPathFromState(location.state);
    const { id } = useParams<{ id: string }>();
    const activityId = id ? Number(id) : null;
    const isEdit = activityId !== null && Number.isInteger(activityId) && activityId > 0;
    // URL 带了 id 但不是正整数（/activity/abc/edit）：不能当新建页，回列表
    useEffect(() => {
        if (id !== undefined && !isEdit) navigate(listPath, { replace: true });
    }, [id, isEdit, navigate, listPath]);
    const [form] = Form.useForm<ActivityFormValues>();
    const { template, loading: templateLoading } = useRuleTemplate();
    const sites = useSiteOptions();
    const [initialized, setInitialized] = useState(false);
    const [originalRules, setOriginalRules] = useState<RuleDraft[]>([]);
    const [showErrors, setShowErrors] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewSource, setPreviewSource] = useState<RuleTextPreviewSource | null>(null);

    const { data: detailData, loading: detailLoading, error: detailError } = useQuery<GetDriverActivityAdminDetailResponse, GetDriverActivityAdminDetailVariables>(
        GET_DRIVER_ACTIVITY_ADMIN_DETAIL,
        { variables: { id: activityId ?? 0 }, skip: !isEdit, fetchPolicy: 'network-only' }
    );
    // 详情拉不到（已删除 / 不存在）：提示后回列表，别停在骨架屏
    useEffect(() => {
        if (!detailError) return;
        showError(detailError);
        navigate(listPath, { replace: true });
    }, [detailError, navigate, listPath]);
    const detail = detailData?.getDriverActivityAdminDetail;
    const status = detail?.status ?? DriverActivityAdminStatus.DRAFT;
    const limited = isEdit && isLimitedEditStatus(status);
    const bannerPreviewUrl = detail?.bannerUrl ?? null;

    const [saveDraft] = useMutation<SaveDriverActivityDraftResponse, SaveDriverActivityVariables>(SAVE_DRIVER_ACTIVITY_DRAFT);
    const [submitOnline] = useMutation<SubmitDriverActivityOnlineResponse, SaveDriverActivityVariables>(SUBMIT_DRIVER_ACTIVITY_ONLINE);
    const [updateLimited] = useMutation<UpdateDriverActivityLimitedResponse, UpdateDriverActivityLimitedVariables>(UPDATE_DRIVER_ACTIVITY_LIMITED);

    // 初值：新建用模板的三语默认文案；编辑用详情
    useEffect(() => {
        if (initialized || templateLoading) return;
        if (isEdit) {
            if (!detail) return;
            if (!canEditActivity(detail.status)) {
                message.warning(t('driverOps.activity.message.notEditable'));
                navigate(`${LIST_PATH}/${detail.id}`, { replace: true, state: location.state });
                return;
            }
            const values = detailToFormValues(detail, template);
            // antd 的 RecursivePartial 会把 Dayjs 对象也展开成可选属性，与 [Dayjs, Dayjs] 元组对不上，这里按原类型放行
            form.setFieldsValue(values as never);
            setOriginalRules(values.rules);
        } else {
            const defaultText: RuleTextDraft = ruleTextFromList(template.defaultTexts, template.languages, template.sections);
            form.setFieldsValue(getDefaultFormValues(defaultText) as never);
        }
        setInitialized(true);
    }, [initialized, templateLoading, isEdit, detail, template, form, navigate, t, location.state]);

    const dateRange = Form.useWatch('dateRange', form) as ActivityFormValues['dateRange'];
    // 活动时间控件：开始只能周一、结束只能周日，且两端选好后回校验不会把已选日期判掉
    const weekRangePicker = useWeekRangePicker();
    // 活动名称：输入法选字期间不校验、不显示超限，选完字后补一次校验
    const nameIme = useImeSafeCount(NAME_MAX_LENGTH, () => {
        form.validateFields(['name']).catch(() => undefined);
    });
    const needSignUp = Form.useWatch('needSignUp', form) as boolean | undefined;

    const weekCount = useMemo(() => computeWeekCount(dateRange?.[0], dateRange?.[1]), [dateRange]);

    const fieldLabels: Record<string, string> = {
        name: t('driverOps.activity.form.name'),
        dateRange: t('driverOps.activity.form.time'),
        signUpDeadline: t('driverOps.activity.form.signUpDeadline'),
        bannerS3Key: t('driverOps.activity.form.banner'),
        maxRewardAmount: t('driverOps.activity.form.maxReward'),
        rules: t('driverOps.activity.form.ruleConfig'),
        ruleText: t('driverOps.activity.form.ruleText'),
    };

    /** t() 的返回类型含 null，Error 构造只收 string */
    const tx = (key: string, opts?: Record<string, string | number>) => String(t(key, opts ?? {}));

    const goBack = () => navigate(listPath);

    /** 受限编辑只校验还能改的三项：被置灰的名称 / 时间 / 截止 / 上限不再按当前配置重跑，免得旧活动被新口径卡死 */
    const validate = async (kind: SubmitKind): Promise<ActivityFormValues | null> => {
        try {
            const values = kind === 'limited'
                ? ((await form.validateFields(LIMITED_FIELDS)) as ActivityFormValues)
                : await form.validateFields();
            setShowErrors(false);
            return values;
        } catch (err) {
            setShowErrors(true);
            const errorFields = (err as { errorFields?: { name: (string | number)[]; errors: string[] }[] }).errorFields ?? [];
            const summary = errorFields
                .map((f) => {
                    const key = String(f.name[0]);
                    return key === 'rules' || key === 'ruleText' ? f.errors[0] : fieldLabels[key] ?? key;
                })
                .join(String(t('driverOps.activity.form.listSeparator')));
            message.error(t('driverOps.activity.validation.submitFailed', { fields: summary }), 3);
            if (errorFields[0]) form.scrollToField(errorFields[0].name, { behavior: 'smooth', block: 'center' });
            return null;
        }
    };

    const handleSubmit = async (kind: SubmitKind) => {
        const values = await validate(kind);
        if (!values) return;
        if (kind === 'limited') {
            if (!activityId) return;
            setSubmitting(true);
            const ok = await submitWithTranslationStaleConfirm(t, (ignore) =>
                updateLimited({ variables: { input: buildLimitedInput(values, template, activityId, ignore) } })
            );
            setSubmitting(false);
            if (ok) {
                message.success(t('driverOps.activity.message.saveSuccess'), 3);
                goBack();
            }
            return;
        }
        if (kind === 'draft') {
            setSubmitting(true);
            const ok = await submitWithTranslationStaleConfirm(t, (ignore) =>
                saveDraft({ variables: { input: buildSaveInput(values, template, activityId, ignore) } })
            );
            setSubmitting(false);
            if (ok) {
                message.success(t('driverOps.activity.message.draftSaved'), 3);
                goBack();
            }
            return;
        }
        const [start] = values.dateRange ?? [null, null];
        confirmOnline({
            t,
            name: values.name ?? '',
            startDate: start ? start.format(DATE_FORMAT) : null,
            signUpDeadline: values.needSignUp && values.signUpDeadline ? values.signUpDeadline.format(DEADLINE_FORMAT) : null,
            onOk: async () => {
                setSubmitting(true);
                const ok = await submitWithTranslationStaleConfirm(t, (ignore) =>
                    submitOnline({ variables: { input: buildSaveInput(values, template, activityId, ignore) } })
                );
                setSubmitting(false);
                if (ok) {
                    message.success(t('driverOps.activity.message.onlineSuccess'), 3);
                    goBack();
                }
            },
        });
    };

    /** 预览用当前表单值，不校验；未填的传空 / 当前值 */
    const openPreview = () => {
        const values = form.getFieldsValue(true) as ActivityFormValues;
        const [start, end] = values.dateRange ?? [null, null];
        setPreviewSource({
            startDate: start ? start.format(DATE_FORMAT) : null,
            endDate: end ? end.format(DATE_FORMAT) : null,
            maxRewardAmount: values.maxRewardAmount !== null && values.maxRewardAmount !== undefined ? amountToString(Number(values.maxRewardAmount)) : null,
            rules: rulesToInput(values.rules ?? []),
            ruleText: ruleTextToInput(values.ruleText ?? {}, template.languages, template.sections),
        });
        setPreviewOpen(true);
    };

    const weekdayLabel = (d: Dayjs) => (isMonday(d) ? t('driverOps.activity.form.monday') : isSunday(d) ? t('driverOps.activity.form.sunday') : '');
    const rangeFormat = (d: Dayjs) => {
        const label = weekdayLabel(d);
        return label ? `${d.format(DATE_FORMAT)}${t('driverOps.activity.form.parenthesized', { text: label })}` : d.format(DATE_FORMAT);
    };

    const pageReady = initialized && !templateLoading && (!isEdit || !detailLoading);
    const basicDisabled = limited;

    return (
        <div>
            <div className="text-[13px] text-[#8C8C8C] mb-2">
                <span className="cursor-pointer" onClick={goBack}>{t('driverOps.activity.title')}</span>
                {' / '}
                <span className="text-[#595959]">{isEdit ? t('driverOps.activity.form.editTitle') : t('driverOps.activity.form.createTitle')}</span>
            </div>
            <div className="flex justify-between items-center mb-[22px]">
                <h2 className="flex items-center gap-3 text-[26px] font-bold m-0">
                    {isEdit ? t('driverOps.activity.form.editTitle') : t('driverOps.activity.form.createTitle')}
                    <ActivityStatusTag status={status} />
                </h2>
                <Button onClick={goBack}>{t('driverOps.activity.action.backToList')}</Button>
            </div>

            {!pageReady ? (
                <div className="bg-white rounded-[10px] p-6">
                    <Skeleton active paragraph={{ rows: 10 }} />
                </div>
            ) : (
                <Form<ActivityFormValues>
                    form={form}
                    labelCol={{ flex: '150px' }}
                    // 长语种标签（西语「Requiere verificación de identidad」）放不下时折行，不压到控件上；中英文标签短，不受影响
                    labelWrap
                    labelAlign="right"
                    wrapperCol={{ flex: '1 1 0%', style: { minWidth: 0 } }}
                    colon={false}
                    scrollToFirstError={false}
                >
                    <SectionCard title={t('driverOps.activity.form.basicInfo')}>
                        <Form.Item
                            label={t('driverOps.activity.form.name')}
                            name="name"
                            required
                            rules={[
                                {
                                    validator: (_, value) => {
                                        // 拼音还没上屏时不校验，避免把拼音字母算进字数误报超长
                                        if (nameIme.composingRef.current) {
                                            return Promise.resolve();
                                        }
                                        const reason = validateName(value);
                                        return reason ? Promise.reject(new Error(tx(`driverOps.activity.validation.name.${reason}`, { max: NAME_MAX_LENGTH }))) : Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input {...nameIme.inputProps} style={{ width: 520 }} showCount disabled={basicDisabled} placeholder={t('driverOps.activity.form.namePlaceholder') as string} />
                        </Form.Item>
                        <Form.Item label={t('driverOps.activity.form.type')} required>
                            <Select
                                style={{ width: 260 }}
                                disabled
                                value={DriverActivityType.ATTENDANCE}
                                options={[{ value: DriverActivityType.ATTENDANCE, label: t(`driverOps.activity.type.${DriverActivityType.ATTENDANCE}`) }]}
                            />
                        </Form.Item>
                        <Form.Item label={t('driverOps.activity.form.time')} required className="mb-0">
                            <div className="flex items-start gap-3">
                                <Segmented options={[{ value: 'WEEK', label: t('driverOps.activity.form.week') }]} value="WEEK" disabled={basicDisabled} />
                                {/* 不用 noStyle：noStyle 会把错误冒泡到外层 Form.Item 再显示一遍（10-22 提示重复） */}
                                <Form.Item
                                    name="dateRange"
                                    className="mb-0"
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                const reason = validateDateRange(value);
                                                return reason ? Promise.reject(new Error(tx(`driverOps.activity.validation.time.${reason}`, { max: MAX_WEEKS }))) : Promise.resolve();
                                            },
                                        },
                                    ]}
                                >
                                    <DatePicker.RangePicker
                                        style={{ width: 360 }}
                                        disabled={basicDisabled}
                                        format={rangeFormat}
                                        placeholder={[t('driverOps.activity.filter.startDate'), t('driverOps.activity.filter.endDate')]}
                                        onFocus={weekRangePicker.onFocus}
                                        onOpenChange={weekRangePicker.onOpenChange}
                                        onCalendarChange={weekRangePicker.onCalendarChange}
                                        disabledDate={weekRangePicker.disabledDate}
                                        cellRender={renderTodayCell}
                                    />
                                </Form.Item>
                            </div>
                            <div className="mt-[6px]">
                                <HelpText>
                                    {t('driverOps.activity.form.weekHelp')}{' '}
                                    <b className="text-[#1F1F1F]">{weekCount ? t('driverOps.activity.form.weekCount', { count: weekCount }) : '--'}</b>
                                </HelpText>
                            </div>
                            <div className="inline-block mt-2 bg-[#F3EEFE] text-[#722ed1] rounded-[6px] px-[10px] py-1 text-[12.5px] font-semibold mb-[18px]">
                                🕒 {t('driverOps.activity.form.timeZoneLine')}
                            </div>
                        </Form.Item>
                        <Form.Item
                            label={t('driverOps.activity.form.signUpDeadline')}
                            name="signUpDeadline"
                            dependencies={['needSignUp', 'dateRange']}
                            extra={
                                <>
                                    <HelpText>
                                        {t(template.allowSignUpDuringActivity ? 'driverOps.activity.form.signUpDeadlineHelpDuring' : 'driverOps.activity.form.signUpDeadlineHelp')}
                                    </HelpText>
                                    {/* 默认配置下截止 ≤ 开始时刻，开始日是今天的需报名活动上线必被截止拦住，提前提示而不是上线才报错 */}
                                    {needSignUp !== false && !template.allowSignUpDuringActivity && isStartDateTodayLa(dateRange?.[0]) && (
                                        <div>
                                            <HelpText error>{t('driverOps.activity.form.signUpDeadlineStartTodayWarn')}</HelpText>
                                        </div>
                                    )}
                                </>
                            }
                            rules={[
                                {
                                    validator: (_, value) => {
                                        const range = form.getFieldValue('dateRange');
                                        const reason = validateSignUpDeadline(value, !!form.getFieldValue('needSignUp'), range?.[0], range?.[1], template.allowSignUpDuringActivity);
                                        return reason ? Promise.reject(new Error(tx(`driverOps.activity.validation.signUpDeadline.${reason}`))) : Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <DatePicker
                                showTime={{ format: 'HH:mm' }}
                                format={DEADLINE_FORMAT}
                                style={{ width: 260 }}
                                disabled={basicDisabled || needSignUp === false}
                                cellRender={renderTodayCell}
                            />
                        </Form.Item>
                        <Form.Item
                            label={t('driverOps.activity.form.banner')}
                            name="bannerS3Key"
                            required
                            className="mb-0"
                            rules={[{ required: true, message: t('driverOps.activity.validation.bannerRequired') as string }]}
                        >
                            <BannerUpload previewUrl={bannerPreviewUrl} />
                        </Form.Item>
                    </SectionCard>

                    <SectionCard title={t('driverOps.activity.form.participation')}>
                        <Form.Item label={t('driverOps.activity.form.needSignUp')} name="needSignUp">
                            <Radio.Group
                                disabled={basicDisabled}
                                onChange={(e) => {
                                    if (!e.target.value) form.setFieldValue('signUpDeadline', null);
                                }}
                            >
                                <Radio value>{t('common.yes')}</Radio>
                                <Radio value={false}>{t('common.no')}</Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item
                            label={t('driverOps.activity.form.needRealName')}
                            name="needRealName"
                            className="mb-0"
                        >
                            <Radio.Group disabled={basicDisabled}>
                                <Radio value>{t('common.yes')}</Radio>
                                <Radio value={false}>{t('common.no')}</Radio>
                            </Radio.Group>
                        </Form.Item>
                    </SectionCard>

                    <SectionCard title={t('driverOps.activity.form.ruleCard')}>
                        <Form.Item
                            label={t('driverOps.activity.form.ruleConfig')}
                            name="rules"
                            required
                            rules={[
                                {
                                    validator: (_, value: RuleDraft[]) => {
                                        const result = validateRules(value ?? [], sites.whsHubMap);
                                        if (result.errors.length) {
                                            const first = result.errors[0];
                                            return Promise.reject(
                                                new Error(tx(`driverOps.activity.validation.rules.${first.reason}`, { ruleNo: first.ruleNo, level: first.level, max: 5 }))
                                            );
                                        }
                                        if (result.conflicts.length) {
                                            const c = result.conflicts[0];
                                            // 规则内「整 Hub + 其下属仓」同时勾选 与 规则间范围覆盖 分开提示，与后端 reason 一致
                                            return Promise.reject(
                                                new Error(
                                                    c.otherRuleIndex === null
                                                        ? tx('driverOps.activity.validation.rules.scopeOverlapWithinRule', { ruleNo: c.ruleIndex + 1 })
                                                        : tx('driverOps.activity.validation.rules.scopeConflict', { ruleNo: c.ruleIndex + 1, otherRuleNo: c.otherRuleIndex + 1 })
                                                )
                                            );
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <RuleConfigTable sites={sites} mode={limited ? 'limited' : 'full'} originalRules={originalRules} showErrors={showErrors} />
                        </Form.Item>
                        <Form.Item
                            label={t('driverOps.activity.form.maxReward')}
                            name="maxRewardAmount"
                            required
                            // 规则金额或活动周数变了也要重算「最高档 × 周数 ≤ 上限」
                            dependencies={['rules', 'dateRange']}
                            className="mb-0"
                            extra={
                                <HelpText>{t('driverOps.activity.form.maxRewardHelp')}</HelpText>
                            }
                            rules={[
                                {
                                    validator: (_, value) => {
                                        // 最高档金额 × 周数 > 上限：由提示改为拦截（10-22 用户定），后端同口径
                                        const range = form.getFieldValue('dateRange') as ActivityFormValues['dateRange'];
                                        const reason = validateMaxReward(value, form.getFieldValue('rules') ?? [], computeWeekCount(range?.[0], range?.[1]));
                                        return reason ? Promise.reject(new Error(tx(`driverOps.activity.validation.maxReward.${reason}`))) : Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <InputNumber prefix="$" style={{ width: 260 }} min={0.01} precision={2} disabled={basicDisabled} placeholder={t('driverOps.activity.form.maxRewardPlaceholder') as string} />
                        </Form.Item>
                    </SectionCard>

                    <SectionCard title={t('driverOps.activity.form.ruleTextCard')}>
                        <Form.Item
                            label={t('driverOps.activity.form.ruleText')}
                            name="ruleText"
                            required
                            className="mb-0"
                            rules={[
                                {
                                    validator: (_, value: RuleTextDraft) => {
                                        const errors = validateRuleText(value ?? {}, template);
                                        if (!errors.length) return Promise.resolve();
                                        const first = errors[0];
                                        return Promise.reject(
                                            new Error(
                                                tx(`driverOps.activity.validation.ruleText.${first.reason}`, {
                                                    lang: String(t(`driverOps.activity.languages.${first.lang}`, { defaultValue: first.lang })),
                                                    // 段标题按文案语种取，和编辑器左列一致（10-22：西语页报「五、发放说明：」）
                                                    section: sectionTitle(i18n.getFixedT(first.lang), first.section),
                                                    max: template.sectionMaxLength,
                                                })
                                            )
                                        );
                                    },
                                },
                            ]}
                        >
                            <RuleTextEditor
                                template={template}
                                extra={
                                    <Button size="small" onClick={openPreview}>
                                        {t('driverOps.activity.preview.button')}
                                    </Button>
                                }
                            />
                        </Form.Item>
                    </SectionCard>

                    <div className="flex justify-end gap-3 border-t border-[#EFEFEF] py-4 px-8 bg-white -mx-6">
                        <Button onClick={goBack}>{t('common.cancel')}</Button>
                        {limited ? (
                            <Button type="primary" loading={submitting} onClick={() => void handleSubmit('limited')}>
                                {t('driverOps.activity.action.save')}
                            </Button>
                        ) : (
                            <>
                                <Button loading={submitting} onClick={() => void handleSubmit('draft')}>
                                    {t('driverOps.activity.action.saveDraft')}
                                </Button>
                                <Button type="primary" loading={submitting} onClick={() => void handleSubmit('online')}>
                                    {t('driverOps.activity.action.submitOnline')}
                                </Button>
                            </>
                        )}
                    </div>
                </Form>
            )}

            {previewSource && (
                <RuleTextPreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} source={previewSource} languages={template.languages} sections={template.sections} />
            )}
        </div>
    );
};

export default DriverActivityFormPage;
